/**
 * Error Handler
 * Simplified JavaScript version for immediate compatibility
 */

// Error codes enumeration
export const ErrorCodes = {
  // Network and API errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  API_TIMEOUT: 'API_TIMEOUT',
  API_ERROR: 'API_ERROR',
  FETCH_FAILED: 'FETCH_FAILED',

  // Authentication errors
  AUTH_INVALID: 'AUTH_INVALID',
  AUTH_EXPIRED: 'AUTH_EXPIRED',
  AUTH_REQUIRED: 'AUTH_REQUIRED',

  // Validation errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  INVALID_FILE_TYPE: 'INVALID_FILE_TYPE',

  // Service errors
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',

  // General errors
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  INITIALIZATION_ERROR: 'INITIALIZATION_ERROR',
};

/**
 * Create an application error
 */
export function createAppError({
  name,
  message,
  code,
  severity = 'medium',
  context = {},
  userFriendly = true,
  retryable = false,
}) {
  const error = new Error(message);
  error.name = name;
  error.code = code;
  error.severity = severity;
  error.context = context;
  error.userFriendly = userFriendly;
  error.retryable = retryable;
  error.timestamp = new Date();

  return error;
}

/**
 * Error Handler Class
 */
class ErrorHandler {
  constructor() {
    this.errors = [];
    this.maxErrors = 50;
    this.errorCounts = {};

    // User-friendly error messages
    this.userMessages = {
      [ErrorCodes.NETWORK_ERROR]: '网络连接出现问题，请检查网络后重试',
      [ErrorCodes.API_TIMEOUT]: '请求超时，请重试',
      [ErrorCodes.AUTH_INVALID]: '登录状态已过期，请重新登录',
      [ErrorCodes.UPLOAD_FAILED]: '图片上传失败，请重试',
      [ErrorCodes.SERVICE_UNAVAILABLE]: '服务暂时不可用，请稍后重试',
      [ErrorCodes.UNKNOWN_ERROR]: '发生未知错误，请重试',
    };
  }

  /**
   * Handle an error
   */
  async handleError(error, options = {}) {
    try {
      // Ensure error has required properties
      const processedError = this.processError(error);

      // Add to error queue
      this.addToQueue(processedError);

      // Log error
      this.logError(processedError);

      // Show user notification if requested
      if (options.showUserNotification !== false && processedError.userFriendly) {
        this.showUserNotification(processedError, options);
      }

      // Report to external services (if available)
      this.reportError(processedError);

    } catch (handlingError) {
      console.error('[ErrorHandler] Error while handling error:', handlingError);
    }
  }

  /**
   * Process error to ensure it has required properties
   */
  processError(error) {
    if (error.code && error.severity) {
      // Already a processed app error
      return error;
    }

    // Convert regular error to app error
    const inferredCode = this.inferErrorCode(error);
    const severity = this.inferSeverity(inferredCode);

    return createAppError({
      name: error.name || 'Error',
      message: error.message || 'Unknown error occurred',
      code: inferredCode,
      severity,
      context: { originalError: error },
      userFriendly: true,
      retryable: this.isRetryable(inferredCode),
    });
  }

  /**
   * Infer error code from error message or type
   */
  inferErrorCode(error) {
    const message = error.message?.toLowerCase() || '';

    if (message.includes('network') || message.includes('fetch')) {
      return ErrorCodes.NETWORK_ERROR;
    }
    if (message.includes('timeout')) {
      return ErrorCodes.API_TIMEOUT;
    }
    if (message.includes('unauthorized') || message.includes('auth')) {
      return ErrorCodes.AUTH_INVALID;
    }
    if (message.includes('upload') || message.includes('file')) {
      return ErrorCodes.UPLOAD_FAILED;
    }

    return ErrorCodes.UNKNOWN_ERROR;
  }

  /**
   * Infer severity from error code
   */
  inferSeverity(code) {
    const severityMap = {
      [ErrorCodes.NETWORK_ERROR]: 'high',
      [ErrorCodes.API_TIMEOUT]: 'medium',
      [ErrorCodes.AUTH_INVALID]: 'high',
      [ErrorCodes.UPLOAD_FAILED]: 'medium',
      [ErrorCodes.SERVICE_UNAVAILABLE]: 'critical',
      [ErrorCodes.VALIDATION_ERROR]: 'low',
      [ErrorCodes.UNKNOWN_ERROR]: 'medium',
    };

    return severityMap[code] || 'medium';
  }

  /**
   * Check if error is retryable
   */
  isRetryable(code) {
    const retryableCodes = [
      ErrorCodes.NETWORK_ERROR,
      ErrorCodes.API_TIMEOUT,
      ErrorCodes.SERVICE_UNAVAILABLE,
    ];

    return retryableCodes.includes(code);
  }

  /**
   * Add error to queue
   */
  addToQueue(error) {
    this.errors.push(error);

    // Maintain max queue size
    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }

    // Update error counts
    this.errorCounts[error.code] = (this.errorCounts[error.code] || 0) + 1;
  }

  /**
   * Log error
   */
  logError(error) {
    const logLevel = this.getLogLevel(error.severity);
    const logMessage = `[${error.severity.toUpperCase()}] ${error.code}: ${error.message}`;

    console[logLevel](logMessage, {
      code: error.code,
      severity: error.severity,
      context: error.context,
      timestamp: error.timestamp,
    });
  }

  /**
   * Get appropriate log level for severity
   */
  getLogLevel(severity) {
    const levelMap = {
      low: 'info',
      medium: 'warn',
      high: 'error',
      critical: 'error',
    };

    return levelMap[severity] || 'warn';
  }

  /**
   * Show user notification
   */
  showUserNotification(error, options = {}) {
    const message = options.userFriendlyMessage ||
                   this.userMessages[error.code] ||
                   '操作失败，请重试';

    const notificationType = this.getNotificationType(error.severity);
    const duration = this.getNotificationDuration(error.severity);

    // Use notification system if available
    if (typeof window !== 'undefined' && window.showNotification) {
      window.showNotification({
        type: notificationType,
        message,
        duration,
        persistent: error.severity === 'critical',
      });
    } else {
      // Fallback to console
      console.warn('[ErrorHandler] User notification:', message);
    }
  }

  /**
   * Get notification type based on severity
   */
  getNotificationType(severity) {
    const typeMap = {
      low: 'info',
      medium: 'warning',
      high: 'error',
      critical: 'error',
    };

    return typeMap[severity] || 'warning';
  }

  /**
   * Get notification duration based on severity
   */
  getNotificationDuration(severity) {
    const durationMap = {
      low: 5000,
      medium: 5000,
      high: 8000,
      critical: 0, // Persistent
    };

    return durationMap[severity] || 5000;
  }

  /**
   * Report error to external services
   */
  reportError(error) {
    // Report to Sentry if available
    if (typeof window !== 'undefined' && window.Sentry) {
      window.Sentry.captureException(error);
    }
  }

  /**
   * Clear error queue
   */
  clearErrors() {
    this.errors = [];
    this.errorCounts = {};
  }

  /**
   * Get error statistics
   */
  getErrorStats() {
    const severityCounts = { low: 0, medium: 0, high: 0, critical: 0 };

    this.errors.forEach(error => {
      severityCounts[error.severity]++;
    });

    return {
      total: this.errors.length,
      bySeverity: severityCounts,
      byCode: { ...this.errorCounts },
      recent: this.errors.slice(-10),
    };
  }
}

// Create singleton instance
export const errorHandler = new ErrorHandler();

// Setup global error handlers
if (typeof window !== 'undefined') {
  // Handle unhandled errors
  window.addEventListener('error', (event) => {
    const error = createAppError({
      name: 'UnhandledError',
      message: event.message || 'Unhandled error occurred',
      code: ErrorCodes.UNKNOWN_ERROR,
      severity: 'high',
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      },
    });

    errorHandler.handleError(error, { showUserNotification: true });
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = createAppError({
      name: 'UnhandledPromiseRejection',
      message: event.reason?.message || 'Unhandled promise rejection',
      code: ErrorCodes.UNKNOWN_ERROR,
      severity: 'medium',
      context: {
        reason: event.reason,
      },
    });

    errorHandler.handleError(error, { showUserNotification: false });

    // Prevent default browser handling
    event.preventDefault();
  });
}